import React, { useState, useEffect, useRef } from 'react';

interface CounterProps {
  value: string;
  duration?: number;
  delay?: number;
}

const Counter: React.FC<CounterProps> = ({ value, duration = 3000, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);

  const numberMatch = value.match(/\d+/);
  const target = numberMatch ? parseInt(numberMatch[0]) : 0;
  const prefix = value.substring(0, value.indexOf(numberMatch?.[0] || ''));
  const suffix = value.substring(value.indexOf(numberMatch?.[0] || '') + (numberMatch?.[0] || '').length);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (elementRef.current) observer.unobserve(elementRef.current);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) observer.observe(elementRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number | null = null;
    let timeoutId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Curva Quintic para suavidade extrema no final
      const easeProgress = 1 - Math.pow(1 - progress, 5);
      
      setCount(Math.floor(easeProgress * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    // Só inicia a animação após o delay solicitado
    timeoutId = window.setTimeout(() => {
      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [isVisible, target, duration, delay]);

  return (
    <span ref={elementRef} className="tabular-nums">
      {prefix}{count}{suffix}
    </span>
  );
};

export default Counter;